import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGrid, Button, Flex, Grid, HelpIndicator, Text, useCopy } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { HighlightText } from "~/pages/Reporting/components/HighlightText";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import { NoMatchesOverlay } from "../NoMatchesOverlay";
import { MERGE_FIELD_PLACEHOLDER } from "./footnotesMapper";
import * as styles from "./styles_vanilla.css";
import type { QueryFootnote } from "./types";

/** Header tooltip for the symbol-merge-field column (mirrors the settings
 * panel's helper copy). */
const SYMBOL_MERGE_FIELD_TOOLTIP =
  "Copy and paste this merge field footnote symbol beside the data field merge field in the template";

/**
 * Single shared copy state for the merge-field-preview column.
 */
type MergeFieldCopyContextValue = {
  copiedKey: string | null;
  copyMergeField: (key: string, value: string) => void;
};

const MergeFieldCopyContext = createContext<MergeFieldCopyContextValue>({
  copiedKey: null,
  copyMergeField: () => {},
});

/** Column header with a help indicator for the symbol column.*/
const SymbolMergeFieldHeader = ({ displayName }: { displayName?: string }) => (
  <Flex alignItems="center" gap={SPACING.X_SMALL}>
    <span className="ag-header-cell-text">{displayName}</span>
    <HelpIndicator id="symbol-merge-field-help" content={SYMBOL_MERGE_FIELD_TOOLTIP}>
      <Icon type="question-circle" variant="outline" size="small" color={Colors.Neutral600} />
    </HelpIndicator>
  </Flex>
);

type FootnotesGridProps = {
  footnotes: QueryFootnote[];
  searchValue?: string;
  /** When true the trailing remove-action column is hidden and row clicks are
   * inert. Defaults to true (view mode). */
  isViewMode?: boolean;
  /** Edit-mode only: opens the footnote settings panel from the row's gear action. */
  onEditFootnote?: (footnoteId: string) => void;
  onDeleteFootnote?: (footnoteId: string) => void;
  /** Edit-mode only: reports the new top-to-bottom row-id order after a drag. */
  onReorderFootnotes?: (orderedIds: string[]) => void;
};

const footnoteMatchesSearch = (footnote: QueryFootnote, searchTerm: string): boolean => {
  const term = searchTerm.toLowerCase();
  return (
    footnote.name.toLowerCase().includes(term) ||
    footnote.symbolMergeFieldPreview.toLowerCase().includes(term)
  );
};

const FootnotesNoRowsOverlay = () => (
  <Flex
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap={SPACING.X_SMALL}
  >
    <Icon
      type="file-alt"
      size="xx-large"
      color={Colors.Neutral600}
      className={styles.noRowsOverlayIcon}
    />
    <Text variant="h4" align="center" className={styles.mutedHeading}>
      No footnotes yet
    </Text>
    <Text variant="secondary-md" align="center">
      Add footnotes to get started
    </Text>
  </Flex>
);

type GridContext = {
  searchValue: string;
  isViewMode: boolean;
};

const NameCellRenderer = (
  params: AgGrid.ICellRendererParams<QueryFootnote, string, GridContext>,
) => {
  const { data } = params;
  if (!data) return null;
  const searchValue = params.context?.searchValue ?? "";
  return (
    <Flex alignItems="center" className={styles.nameCell}>
      <Text lines={1} className={styles.nameText}>
        <HighlightText text={data.name} searchValue={searchValue} />
      </Text>
    </Flex>
  );
};

const SymbolMergeFieldPreviewCellRenderer = (
  params: AgGrid.ICellRendererParams<QueryFootnote, string, GridContext>,
) => {
  const value = params.value ?? "";
  const footnoteId = params.data?.id;
  const colId = params.column?.getColId();
  const searchValue = params.context?.searchValue ?? "";
  const isViewMode = params.context?.isViewMode ?? true;
  const { copiedKey, copyMergeField } = useContext(MergeFieldCopyContext);

  // An empty value means the server hasn't minted this footnote's slug yet,
  // which only happens before its first save.
  const hasToken = value.length > 0;

  const cellKey = footnoteId !== undefined && colId ? `${footnoteId}:${colId}` : undefined;

  const handleCopy = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (cellKey && hasToken) copyMergeField(cellKey, value);
    },
    [copyMergeField, cellKey, value, hasToken],
  );

  const isCopied = cellKey !== undefined && copiedKey === cellKey;

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap={SPACING.SMALL}
      className={styles.mergeFieldCell}
    >
      <Text variant="regular-sm" className={styles.mergeFieldText}>
        {hasToken ? (
          <HighlightText text={value} searchValue={searchValue} />
        ) : (
          MERGE_FIELD_PLACEHOLDER
        )}
      </Text>
      {isViewMode && hasToken ? (
        <Button
          icon={isCopied ? "check" : "copy"}
          variant="muted"
          onClick={handleCopy}
          aria-label="Copy symbol merge field"
          className={styles.copyButton}
        />
      ) : null}
    </Flex>
  );
};

type ActionsCellProps = AgGrid.ICellRendererParams<QueryFootnote, unknown> & {
  onEditFootnote?: (footnoteId: string) => void;
  onDeleteFootnote?: (footnoteId: string) => void;
};

const ActionsCellRenderer = ({ data, onEditFootnote, onDeleteFootnote }: ActionsCellProps) => {
  if (!data) return null;
  return (
    <Flex
      alignItems="center"
      justifyContent="flex-end"
      gap={SPACING.X_SMALL}
      className={styles.actionsCell}
    >
      <Button
        variant="muted"
        icon="cog"
        aria-label={`Edit ${data.name}`}
        data-testid={`edit-footnote-${data.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onEditFootnote?.(data.id);
        }}
      />
      <Button
        variant="muted"
        icon="trash"
        aria-label={`Remove ${data.name}`}
        data-testid={`remove-footnote-${data.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onDeleteFootnote?.(data.id);
        }}
      />
    </Flex>
  );
};

export const FootnotesGrid = ({
  footnotes,
  searchValue = "",
  isViewMode = true,
  onEditFootnote,
  onDeleteFootnote,
  onReorderFootnotes,
}: FootnotesGridProps) => {
  const { copy, attemptedCopy } = useCopy({ useTimeout: true });
  const [lastCopiedKey, setLastCopiedKey] = useState<string | null>(null);

  // Reordering only makes sense edit-mode
  const canReorder =
    !isViewMode && searchValue.trim().length === 0 && Boolean(onReorderFootnotes);

  const copyMergeField = useCallback(
    (key: string, value: string) => {
      setLastCopiedKey(key);
      void copy(value);
    },
    [copy],
  );

  const copyContextValue = useMemo<MergeFieldCopyContextValue>(
    () => ({
      copiedKey: attemptedCopy ? lastCopiedKey : null,
      copyMergeField,
    }),
    [attemptedCopy, lastCopiedKey, copyMergeField],
  );

  const gridContext = useMemo<GridContext>(
    () => ({ searchValue, isViewMode }),
    [searchValue, isViewMode],
  );

  const rowData = useMemo<QueryFootnote[]>(() => {
    const trimmed = searchValue.trim();
    return trimmed
      ? footnotes.filter((footnote) => footnoteMatchesSearch(footnote, trimmed))
      : footnotes;
  }, [footnotes, searchValue]);

  // Footnotes are an ordered list (marker `(1)` derives from position). Order is
  // set by drag-to-reorder, never column sort — so sorting stays disabled and
  // the on-screen order matches the PDF.
  const columnDefs = useMemo<AgGrid.ColDef<QueryFootnote>[]>(() => {
    const defs: AgGrid.ColDef<QueryFootnote>[] = [
      {
        colId: "name",
        field: "name",
        headerName: "Name",
        minWidth: 200,
        pinned: "left",
        lockPinned: true,
        suppressMovable: true,
        suppressSizeToFit: true,
        // Drag handle lives on the name cell
        rowDrag: canReorder,
        cellRenderer: NameCellRenderer,
      },
      {
        colId: "symbolMergeFieldPreview",
        field: "symbolMergeFieldPreview",
        headerName: "Symbol merge field preview",
        headerComponent: SymbolMergeFieldHeader,
        minWidth: 220,
        cellRenderer: SymbolMergeFieldPreviewCellRenderer,
      },
      {
        field: "footnoteType",
        headerName: "Footnote type",
        minWidth: 160,
      },
      {
        field: "suppressionLogic",
        headerName: "Suppression logic",
        minWidth: 160,
      },
    ];

    if (!isViewMode) {
      defs.push({
        colId: "actions",
        headerName: "",
        pinned: "right",
        lockPinned: true,
        sortable: false,
        filter: false,
        suppressMovable: true,
        suppressSizeToFit: true,
        resizable: false,
        minWidth: 120,
        maxWidth: 120,
        cellClass: styles.pinnedActionsColumnCell,
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: {
          onEditFootnote,
          onDeleteFootnote,
        },
      });
    }

    return defs;
  }, [isViewMode, onEditFootnote, onDeleteFootnote, canReorder]);

  // Managed dragging: AG Grid reorders its row model live; on drop we read the
  // resulting order and hand it to the parent, which owns `footnoteRows` and
  // reflows it back through `rowData` (re-indexing `sortOrder` on Save).
  const handleRowDragEnd = useCallback(
    (event: AgGrid.RowDragEndEvent<QueryFootnote>) => {
      if (!onReorderFootnotes) return;
      const orderedIds: string[] = [];
      event.api.forEachNode((node) => {
        if (node.data) orderedIds.push(node.data.id);
      });
      if (orderedIds.length === 0) return;
      onReorderFootnotes(orderedIds);
    },
    [onReorderFootnotes],
  );

  const { gridOptions } = useAgGridDefaults({
    // `useAgGridDefaults` enables `sortable`/`filter` by default; footnotes are
    // an ordered list so both are turned off here (the per-column defs above
    // omit `sortable`, and the actions column keeps `sortable: false`).
    defaultColDefOverrides: {
      sortable: false,
      filter: false,
      menuTabs: [],
    },
    overrides: {
      domLayout: "normal",
      rowHeight: 42,
      headerHeight: 48,
      suppressContextMenu: true,
      suppressCellFocus: true,
      pagination: false,
      sideBar: false,
      statusBar: undefined,
      autoSizeStrategy: undefined,
    },
  });

  const gridApiRef = useRef<AgGrid.GridApi<QueryFootnote> | null>(null);
  const handleGridReady = useCallback((event: AgGrid.GridReadyEvent<QueryFootnote>) => {
    gridApiRef.current = event.api;
  }, []);

  useEffect(() => {
    gridApiRef.current?.refreshCells({
      columns: ["name", "symbolMergeFieldPreview"],
      force: true,
    });
  }, [searchValue, isViewMode]);

  const trimmedSearch = searchValue.trim();
  const noRowsOverlayComponent = useMemo(
    () => (footnotes.length > 0 && trimmedSearch ? NoMatchesOverlay : FootnotesNoRowsOverlay),
    [footnotes.length, trimmedSearch],
  );

  return (
    <MergeFieldCopyContext.Provider value={copyContextValue}>
      <Grid<QueryFootnote>
        rowData={rowData}
        getRowId={(params) => params.data.id}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        context={gridContext}
        onGridReady={handleGridReady}
        noRowsOverlayComponent={noRowsOverlayComponent}
        rowDragManaged={canReorder}
        animateRows={canReorder}
        onRowDragEnd={handleRowDragEnd}
      />
    </MergeFieldCopyContext.Provider>
  );
};
